import { Component, Output, EventEmitter, Input, DestroyRef, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoanApplicationService } from '../../loan-applications/loan-applications.service';
import { LoanApplicationItem, LoanApplicationUpdateDTO } from '../../loan-applications/loan-applications.interface';
import { UserLoginService } from 'src/app/login/userlogin.service';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { LosStateService } from '../los-state.service';

@Component({
  selector: 'app-disbursed',
  templateUrl: './disbursed.component.html',
  styleUrls: ['./disbursed.component.css']
})
export class DisbursedComponent implements OnInit {
  @Input() applicationId: number | null = null;
  @Output() prev = new EventEmitter<void>();

  private loanApplicationService = inject(LoanApplicationService);
  private userLoginService = inject(UserLoginService);
  private losState = inject(LosStateService);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);

  applicationData: LoanApplicationItem | null = null;

  disbursalSuccess = false;
  disbursalFailed = false;
  isDisbursing = false;
  isRetrying = false;

  get isDisbursed(): boolean {
    return this.applicationData?.isDisbursed === 1;
  }

  ngOnInit(): void {
    this.losState.applicationData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(app => {
        this.applicationData = app;
        if (app?.isDisbursed === 1) {
          this.disbursalSuccess = true;
        }
      });
  }

  async markDisbursed(): Promise<void> {
    if (!this.applicationData || !this.applicationId) return;
    if (this.isDisbursing) return;
    this.isDisbursing = true;

    try {
      const payload: LoanApplicationUpdateDTO = {
        applicationId: this.applicationData.applicationId,
        userId: this.applicationData.userId,
        productId: this.applicationData.productId,
        applicationStatus: 'Disbursed',
        requestedAmount: this.applicationData.requestedAmount,
        approvedAmount: this.applicationData.approvedAmount,
        tenureMonths: this.applicationData.tenureMonths,
        applicationDate: this.applicationData.applicationDate,
        approvalDate: this.applicationData.approvalDate,
        approvedBy: this.applicationData.approvedBy || '',
        rejectionReason: this.applicationData.rejectionReason || '',
        rejectedBy: this.applicationData.rejectedBy || '',
        isDisbursed: 1,
        disbursedOn: new Date(),
        disbursedAmount: this.applicationData.approvedAmount || this.applicationData.requestedAmount,
        subUserId: this.applicationData.subUserId,
        businessId: this.applicationData.businessId,
        productCode: this.applicationData.productCode || '',
        prrn: this.applicationData.prrn,
        lmsApplicationName: this.applicationData.lmsApplicationName,
        otherDetail1: this.applicationData.otherDetail1,
        otherDetail2: this.applicationData.otherDetail2,
        otherDetail3: this.applicationData.otherDetail3,
        otherDetail4: this.applicationData.otherDetail4,
        otherDetail5: this.applicationData.otherDetail5,
        otherDetail6: this.applicationData.otherDetail6,
        isActive: 1,
        actionUser: this.userLoginService.User()?.userId?.toString() || '0'
      };

      const updated = await firstValueFrom(this.loanApplicationService.LoanApplication_Update(payload));
      this.losState.patchApplication(updated);
      this.disbursalSuccess = true;
      this.disbursalFailed = false;
      this.isDisbursing = false;
      this.toastr.success('Loan disbursed successfully');
    } catch (err) {
      this.disbursalFailed = true;
      this.disbursalSuccess = false;
      this.isDisbursing = false;
      this.toastr.error('Disbursal failed. Please retry.');
      console.error('Disbursal error:', err);
    }
  }

  async retryDisbursal(): Promise<void> {
    this.isRetrying = true;
    this.disbursalFailed = false;
    await this.markDisbursed();
    this.isRetrying = false;
  }

  returnToDashboard(): void {
    this.router.navigate(['/pay-later/credit-applications']);
  }
}
